/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Radio, message, Upload, Select, DatePicker, Alert, Space } from 'antd'
import {
  LoadingOutlined,
  PictureOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'
import { Controller, set, useForm } from 'react-hook-form'
import { Button } from '../Button'
import { Input } from '../Input'
import { createErrorMessage } from '@sportninja/common/utils/utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import EditEntitySubTitle from 'src/pages/EditEntitySubTitle'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import banners from '../Form/banner-helpers'
import {
  createOrg,
  createSchedule,
  editOrg,
  updateSchedule,
} from 'src/helpers/EntityCreationHelper'
import OptionButtonDelete from '../List/OptionButtons/Delete'
import EntityFormDelete from '../Form/EntityFormDelete'
import DeleteModal from '../Modal/DeleteModal'
import Icon from '../Icon'
import req from '@sportninja/common/api/request'
import { Tabs } from 'antd'
import ToggleSection from 'src/pages/Schedule/EditRegistration/ToggleSection'
import { init } from '@sentry/react'
import CopyComponent from '../CopyComponent'
import './styles.scss'
import { useSports } from '@sportninja/common/hooks/useSports'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'
import PhoneInputComponent from '../PhoneFormComponent'
import Banner from '../Banner'
import { getErrorMessage } from '@sportninja/common/utils/utils'

dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)

const labelById = {
  0: 'General',
  1: 'Settings',
  // 2: 'Integrations',
}

const NewScheduleForm = ({
  initialState = null,
  onCancel,
  triggerRefresh,
  parentOrgId,
  displayCanlanForm = false,
}) => {
  const [value, setValue] = useState(initialState?.name_full || '')
  const [isLoading, setIsLoading] = useState(false)
  console.log('initialState', initialState)
  const [privacySettings, setPrivacySettings] = useState(
    initialState && initialState?.is_public ? 'public' : 'private'
  )
  const [imageUrl, setImageUrl] = useState(
    initialState?.image?.full_path || null
  )
  const [imageChanged, setImageChanged] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const isEdit = initialState?.name_full ? true : false
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(
    initialState?.is_registration_on || false
  )
  const { sports } = useSports()
  const [selectedSport, setSelectedSport] = useState(
    initialState ? initialState.sport_id : null
  )
  const [competitionDateRange, setCompetitionDateRange] = useState({
    starts_at: dayjs().utc().format('YYYY-MM-DD'),
    ends_at: dayjs().utc().format('YYYY-MM-DD'),
  })
  const [phoneNumber, setPhoneNumber] = useState(initialState?.phone || '')
  const [fieldErrors, setFieldErrors] = useState([])
  const [customError, setCustomError] = useState({
    name: null,
    sport: null,
    starts_at: null,
    ends_at: null,
    abbreviation: null,
    phone: null,
    website: null,
  })
  const seasonTypes = [
    {
      label: 'Spring / Summer',
      value: 'Spring / Summer',
    },
    {
      label: 'Fall / Winter',
      value: 'Fall / Winter',
    },
    {
      label: 'Tournament',
      value: 'Tournament',
    },
  ]
  const [selectedSeasonType, setSelectedSeasonType] = useState(null)
  const [onlineRegistrationOpen, setOnlineRegistrationOpen] = useState(false)
  const seasonCurrencies = [
    {
      label: 'USD',
      value: 'USD',
    },
    {
      label: 'CAD',
      value: 'CAD',
    },
  ]
  const [selectedSeasonCurrency, setSelectedSeasonCurrency] = useState(null)

  const setCustomErrors = (invalid_fields) => {
    if (invalid_fields['name_full']) {
      setCustomErroByField('name_full', invalid_fields['name_full'])
    }
    if (invalid_fields['name']) {
      setCustomErroByField('name_full', invalid_fields['name_full'])
    }
    if (invalid_fields['starts_at']) {
      setCustomErroByField('starts_at', invalid_fields['starts_at'])
    }
    if (invalid_fields['ends_at']) {
      setCustomErroByField('ends_at', invalid_fields['ends_at'])
    }
    if (invalid_fields['sport_id']) {
      setCustomErroByField('sport_type', invalid_fields['sport_id'])
    }
    if (invalid_fields['sport_type']) {
      setCustomErroByField('sport_type', invalid_fields['sport_type'])
    }
  }

  const setCustomErroByField = (field, message) => {
    setCustomError((prev) => ({ ...prev, [field]: message }))
    setFieldErrors((prevErrors) => [...new Set([...prevErrors, field])])
  }

  const {
    control,
    setValue: setFormValue,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      name_full: initialState?.name_full || '',
      abbreviation: initialState?.abbreviation || '',
      phone: initialState?.phone || '',
      website: initialState?.website || '',
      email: initialState?.email || '',
      privacy_settings:
        initialState?.is_public === true
          ? 'public'
          : initialState?.is_public === false
          ? 'private'
          : 'public',
    },
  })

  const createAbbreviation = (companyName) => {
    let text = companyName.replace(/\s/g, '') // Remove all whitespaces
    // clear any special characters or emojis
    text = text.replace(/[^a-zA-Z0-9]/g, '')
    // Check if input is valid
    if (!text) {
      return '-' // Return an empty string if the input is empty or invalid
    }
    if (typeof text !== 'string') {
      return '-' // Return an empty string if the input is not a string
    }
    if (text.length < 5) {
      return text.toUpperCase() // Return the input in uppercase if it has less than 5 characters
    }
    // Get the first 5 characters of the text
    const abbreviation = text.slice(0, 5)

    // Ensure the abbreviation has at least one character and return it in uppercase
    return abbreviation.length > 0 ? abbreviation.toUpperCase() : ''
  }

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      setError(null)
      let response
      if (!isEdit) {
        response = await createSchedule(
          {
            ...data,
            starts_at: competitionDateRange.starts_at,
            ends_at: competitionDateRange.ends_at,
            is_registration_on: isRegistrationEnabled,
            sport_type: sports.find((sport) => sport.id === selectedSport)
              ?.name,
            sport_id: selectedSport,
            is_public: privacySettings === 'public' ? true : false,
            name: data?.name_full,
            phone: phoneNumber,
            settings: displayCanlanForm
              ? {
                  season_type: selectedSeasonType,
                  season_currency: selectedSeasonCurrency,
                  tax_rate: data?.tax_rate,
                  online_registration_open: onlineRegistrationOpen,
                }
              : {},
          },
          parentOrgId,
          imageUrl
        )
      } else {
        response = await updateSchedule(
          {
            ...data,
            starts_at: competitionDateRange.starts_at,
            ends_at: competitionDateRange.ends_at,
            is_registration_on: isRegistrationEnabled,
            sport_type: sports.find((sport) => sport.id === selectedSport)
              ?.name,
            is_public: privacySettings === 'public' ? true : false,
            name: data?.name_full,
            phone: phoneNumber,
            settings: displayCanlanForm
              ? {
                  season_type: selectedSeasonType,
                  season_currency: selectedSeasonCurrency,
                  tax_rate: data?.tax_rate,
                  online_registration_open: onlineRegistrationOpen,
                }
              : {},
          },
          initialState?.id,
          imageUrl
        )
      }
      onCancel()
      if (isEdit) {
        banners.entityUpdate(ENTITY_TYPES.schedule)(response)
      } else {
        banners.entityCreate(ENTITY_TYPES.schedule)(response)
      }
      if (!isEdit) {
        setTimeout(() => {
          window.location.reload()
        }, 5000)
      } else {
        triggerRefresh()
        onCancel()
      }
    } catch (e) {
      if (e?.invalid_fields) {
        setCustomErrors(e.invalid_fields)
        setLoading(false)
        return
      }
      const errorMessage = getErrorMessage(e)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const onCancelPressed = () => {
    if (value && !initialState) {
      setValue('')
      setFormValue('name_full', '')
      setFormValue('abbreviation', '')
      setFormValue('phone', '')
      setFormValue('website', '')
      setFormValue('email', '')
      setFormValue('public')
      setPrivacySettings('public')
      setImageUrl(null)
      setPhoneNumber('')
      setCompetitionDateRange({
        starts_at: dayjs().utc(),
        ends_at: dayjs().utc(),
      })
      setFieldErrors([])
      setCustomError({
        name: null,
        sport: null,
        starts_at: null,
        ends_at: null,
        abbreviation: null,
        phone: null,
        website: null,
      })
    } else {
      onCancel()
    }
  }

  const getBase64 = (img, callback) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => callback(reader.result))
    if (img) {
      reader.readAsDataURL(img)
    }
  }

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!')
      setError('You can only upload JPG/PNG file!')
    }
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error('Image must smaller than 2MB!')
      setError('Image must smaller than 2MB!')
    }
    return isJpgOrPng && isLt2M
  }

  const handleChange = (info) => {
    try {
      setImageChanged(true)
      setLoading(true)
      getBase64(info.file.originFileObj, (url) => {
        setLoading(false)
        setImageUrl(url)
      })
    } finally {
      setLoading(false)
    }
  }

  const uploadButton = (
    <div>
      {loading ? (
        <LoadingOutlined />
      ) : (
        <PictureOutlined
          style={{
            fontSize: 16,
            color: colors.ATTENDANCE_GRAY,
          }}
        />
      )}
      <div
        style={{
          marginTop: 8,
          color: colors.ATTENDANCE_GRAY,
          fontSize: 12,
        }}
      >
        Upload
      </div>
    </div>
  )

  const GeneralSettingsForm = () => {
    return (
      <div
        css={css`
          padding-top: 16px;
          padding-left: 16px;
          padding-right: 16px;
          display: flex;
          flex: 1;
          flex-direction: column;
          height: 100%;
          overflow-y: auto;
          max-height: 525px;
        `}
      >
        {/* Title */}
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            width: 100%;
            align-items: center;
            margin-bottom: 16px;
          `}
        >
          <p
            css={css`
              color: var(--Neutrals-White, #fff);
              font-size: 24px;
              font-style: normal;
              font-weight: 600;
              line-height: 120%; /* 28.8px */
              font-family: ${isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'Rift, Arial, Helvetica, sans-serif'};
              font-weight: 700;
            `}
          >
            General Info
          </p>
          {isEdit ? (
            <div>
              <CopyComponent id={initialState?.id} />
            </div>
          ) : null}
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex: 1;
          `}
        >
          <style>
            {`
          .ant-input {
            background-color: ${colors.HEADER};
          }
          :where(.css-dev-only-do-not-override-11bjtgs).ant-upload-wrapper.ant-upload-picture-card-wrapper .ant-upload.ant-upload-select, :where(.css-dev-only-do-not-override-11bjtgs).ant-upload-wrapper.ant-upload-picture-circle-wrapper .ant-upload.ant-upload-select {
            background-color: ${colors.HEADER};
            width: 90px;
            height: 90px;
          }
        `}
          </style>
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: flex-start;
              flex: 1;
              gap: 16px;
              margin-bottom: 8px;
            `}
          >
            <Upload
              name='avatar'
              listType='picture-circle'
              className='avatar-uploader'
              showUploadList={false}
              action={null}
              beforeUpload={beforeUpload}
              onChange={handleChange}
              css={css`
                display: flex;
                flex: 0.2;
                border-color: ${colors.DEFAULT_FLAIR};
              `}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt='avatar'
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                  }}
                />
              ) : (
                uploadButton
              )}
            </Upload>
            {isEdit && imageUrl ? (
              <CloseCircleOutlined
                onClick={() => {
                  setImageUrl(null)
                }}
                style={{
                  color: colors.FIERY_RED,
                  fontSize: 24,
                }}
              />
            ) : null}
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-bottom: 8px;
            `}
          >
            <Input
              fieldName='name_full'
              control={control}
              label='Competition Name *'
              invalid={
                fieldErrors?.name_full || fieldErrors.includes('name_full')
              }
              invalidMessage={
                customError?.name_full || 'This field is mandatory'
              }
            />
            <Input
              fieldName='abbreviation'
              control={control}
              label='Abbreviation (8 or less characters)'
              maxLength={8}
              invalid={
                fieldErrors?.abbreviation ||
                fieldErrors.includes('abbreviation')
              }
              invalidMessage={
                customError?.abbreviation || 'This field is mandatory'
              }
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-bottom: 8px;
            `}
          >
            <div
              css={css`
                flex: 1;
                margin-bottom: 6px;
                .ant-picker {
                  background-color: ${colors.HEADER};
                }
              `}
            >
              <p
                tabIndex={-1}
                css={css`
                  font-weight: 500;
                  font-size: 12px;
                  line-height: 12px;
                  color: ${colors.WHITE};
                  margin-bottom: 8px;
                `}
              >
                Start and End Date *
              </p>
              <DatePicker.RangePicker
                // Use this to allow the date picker to be displayed in the modal
                getPopupContainer={(triggerNode) => {
                  return triggerNode.parentNode
                }}
                style={{ width: '100%' }}
                size='large'
                placeholder={['Start', 'End']}
                onChange={(e) => {
                  if (e) {
                    const [starts_at, ends_at] = e
                    setCompetitionDateRange({
                      starts_at: dayjs(starts_at).utc().format('YYYY-MM-DD'),
                      ends_at: dayjs(ends_at).utc().format('YYYY-MM-DD'),
                    })
                  }
                }}
                value={[
                  dayjs(competitionDateRange.starts_at),
                  dayjs(competitionDateRange.ends_at),
                ]}
              />
              <div
                css={css`
                  transition: opacity 0.1s ease-in-out;
                  opacity: ${fieldErrors.includes('starts_at') ||
                  fieldErrors.includes('ends_at')
                    ? 1
                    : 0};
                  font-size: 12px;
                  color: ${colors.ERROR_LIGHT};
                `}
              >
                <span>
                  {customError.starts_at ||
                    customError.ends_at ||
                    'Invalid date'}
                </span>
              </div>
            </div>
            <div
              css={css`
                .ant-select {
                  background-color: ${colors.HEADER};
                  width: 100%;
                }
                flex: 1;
                margin-bottom: 8px;
              `}
            >
              <p
                tabIndex={-1}
                css={css`
                  font-weight: 500;
                  font-size: 12px;
                  line-height: 12px;
                  color: ${colors.WHITE};
                  margin-bottom: 8px;
                `}
              >
                Sport Type *
              </p>
              <Select
                disabled={isEdit}
                showSearch={false}
                onChange={(e) => {
                  setSelectedSport(e)
                }}
                options={sports?.map((sport) => ({
                  label:
                    sport?.name.charAt(0).toUpperCase() + sport?.name.slice(1),
                  value: sport?.id,
                }))}
                value={selectedSport}
              />
              <div
                css={css`
                  transition: opacity 0.1s ease-in-out;
                  opacity: ${fieldErrors.includes('sport_type') ||
                  fieldErrors.includes('sport')
                    ? 1
                    : 0};
                  font-size: 12px;
                  color: ${colors.ERROR_LIGHT};
                `}
              >
                <span>
                  {customError.sport_type ||
                    customError.sport ||
                    'Invalid date'}
                </span>
              </div>
            </div>
          </div>
          {/* Removing this as requested by Jeff. */}
          {/* <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-bottom: 8px;
            `}
          >
            <div
              css={css`
                margin-bottom: 8px;
                flex: 1;
              `}
            >
              <style>
                {`
                  .flag-dropdown {
                    height: 40px!important;
                  }
                `}
              </style>
              <p
                tabIndex={-1}
                css={css`
                  font-weight: 500;
                  font-size: 12px;
                  line-height: 12px;
                  color: ${colors.WHITE};
                  margin-bottom: 8px;
                `}
              >
                Phone
              </p>
              <PhoneInputComponent
                phoneValue={phoneNumber}
                namePhone={'phoneNumber'}
                onChangePhone={(e) => setPhoneNumber(e.target.value)}
                setPhoneValue={setPhoneNumber}
                containerStyle={{
                  width: '100%',
                  height: 40,
                }}
                inputStyle={{
                  borderWidth: 1,
                  borderColor: '#3F4753',
                  height: 40,
                  borderRadius: 4,
                  display: 'flex',
                  backgroundColor: 'transparent',
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: 16,
                  letterSpacing: 0.74,
                  width: '100%',
                }}
                disableDropdown={true}
              />
              <div
                css={css`
                  transition: opacity 0.1s ease-in-out;
                  opacity: ${fieldErrors.includes('phone') ? 1 : 0};
                  font-size: 12px;
                  color: ${colors.ERROR_LIGHT};
                `}
              >
                <span>{customError.phone || 'Invalid phone number'}</span>
              </div>
            </div>
            <Input
              fieldName='website'
              control={control}
              label='Website'
              required={false}
              invalid={fieldErrors?.website || fieldErrors.includes('website')}
              invalidMessage={customError?.website || 'This field is mandatory'}
            />
          </div> */}
          {displayCanlanForm ? (
            <>
              <div
                css={css`
                  border-bottom: 1px solid #3f4753;
                  margin-top: 16px;
                  margin-bottom: 16px;
                `}
              />
              <div
                css={css`
                  display: flex;
                  gap: 16px;
                `}
              >
                <div
                  css={css`
                    .ant-select {
                      background-color: ${colors.HEADER};
                      width: 100%;
                    }
                    flex: 1;
                    margin-bottom: 8px;
                  `}
                >
                  <p
                    tabIndex={-1}
                    css={css`
                      font-weight: 500;
                      font-size: 12px;
                      line-height: 12px;
                      color: ${colors.WHITE};
                      margin-bottom: 8px;
                    `}
                  >
                    Season Type
                  </p>
                  <Select
                    showSearch={false}
                    onChange={(e) => {
                      setSelectedSeasonType(e)
                    }}
                    options={seasonTypes}
                    value={selectedSeasonType}
                  />
                  <div
                    css={css`
                      transition: opacity 0.1s ease-in-out;
                      opacity: ${fieldErrors.includes('season_type') ? 1 : 0};
                      font-size: 12px;
                      color: ${colors.ERROR_LIGHT};
                    `}
                  >
                    <span>
                      {customError.season_type || 'Invalid Season Type'}
                    </span>
                  </div>
                </div>
                <div
                  css={css`
                    .ant-select {
                      background-color: ${colors.HEADER};
                      width: 100%;
                    }
                    flex: 1;
                    margin-bottom: 8px;
                  `}
                >
                  <p
                    tabIndex={-1}
                    css={css`
                      font-weight: 500;
                      font-size: 12px;
                      line-height: 12px;
                      color: ${colors.WHITE};
                      margin-bottom: 8px;
                    `}
                  >
                    Season Currency
                  </p>
                  <Select
                    showSearch={false}
                    onChange={(e) => {
                      setSelectedSeasonCurrency(e)
                    }}
                    options={seasonCurrencies}
                    value={selectedSeasonCurrency}
                  />
                  <div
                    css={css`
                      transition: opacity 0.1s ease-in-out;
                      opacity: ${fieldErrors.includes('season_currency')
                        ? 1
                        : 0};
                      font-size: 12px;
                      color: ${colors.ERROR_LIGHT};
                    `}
                  >
                    <span>
                      {customError.season_currency || 'Invalid Season Currency'}
                    </span>
                  </div>
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  gap: 16px;
                `}
              >
                <Input
                  fieldName='tax_rate'
                  control={control}
                  label='Tax Rate (%)'
                  required={false}
                  customWidth='50%'
                />
                <ToggleSection
                  title='Registration'
                  description='Online Registration Open?'
                  isEnabled={onlineRegistrationOpen}
                  onClick={() => {
                    setOnlineRegistrationOpen((o) => !o)
                  }}
                />
              </div>
              <div
                css={css`
                  border-bottom: 1px solid #3f4753;
                  margin-top: 16px;
                  margin-bottom: 16px;
                `}
              />
            </>
          ) : null}
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 12px;
              margin-bottom: 8px;
              flex-direction: column;
            `}
          >
            <p
              tabIndex={-1}
              css={css`
                font-weight: 500;
                font-size: 12px;
                line-height: 12px;
                color: ${colors.WHITE};
              `}
            >
              Visibility
            </p>
            <Controller
              name='privacy_settings'
              control={control}
              defaultValue='public'
              rules={{ required: true }}
              render={({ field }) => {
                return (
                  <Radio.Group
                    {...field}
                    value={privacySettings}
                    defaultValue={'public'}
                    onChange={(e) => {
                      setPrivacySettings(e.target.value)
                      field.onChange(e)
                    }}
                  >
                    <Radio.Button value='public'>Public</Radio.Button>
                    <Radio.Button value='private'>Private</Radio.Button>
                  </Radio.Group>
                )
              }}
            />
            {privacySettings && privacySettings === 'public' ? (
              <p
                tabIndex={-1}
                css={css`
                  font-weight: 500;
                  font-size: 12px;
                  line-height: 12px;
                  color: ${colors.WHITE};
                  margin-bottom: 8px;
                `}
              >
                Everyone can search, view details, and follow your Organization.
              </p>
            ) : privacySettings && privacySettings === 'private' ? (
              <p
                tabIndex={-1}
                css={css`
                  font-weight: 500;
                  font-size: 12px;
                  line-height: 12px;
                  color: ${colors.WHITE};
                  margin-bottom: 8px;
                `}
              >
                Private Competitions can only be viewed and followed by people
                that have been invited by admins.
              </p>
            ) : null}
          </div>
          <div
            css={css`
              position: sticky;
              bottom: 0;
              background-color: ${colors.HEADER};
              z-index: 100;
              padding-bottom: 16px;
              padding-top: 16px;
            `}
          >
            {error ? (
              <Space
                direction='vertical'
                style={{ width: '100%', marginBottom: 8 }}
              >
                <Alert
                  message='Error'
                  description={error || 'An error occurred. Please try again.'}
                  type='error'
                  showIcon
                  closable
                />
              </Space>
            ) : null}
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 16px;
              `}
            >
              <Button
                onClick={(e) => {
                  e.preventDefault()
                  onCancelPressed()
                }}
                label={value && !initialState ? 'Clear' : 'Cancel'}
                variant='secondary'
                disabled={isLoading}
              />
              <Button
                onClick={handleSubmit(onSubmit)}
                type='submit'
                label='Submit'
                isLoading={isLoading}
              />
            </div>
          </div>
        </form>
      </div>
    )
  }

  const SettingsForm = () => {
    return (
      <div
        css={css`
          padding-top: 16px;
          padding-left: 16px;
          padding-right: 16px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: flex-start;
          flex: 1;
          height: 100%;
        `}
      >
        <div
          css={css`
            width: 100%;
          `}
        >
          {/* Title */}
          <p
            css={css`
              color: var(--Neutrals-White, #fff);
              font-size: 24px;
              font-style: normal;
              font-weight: 600;
              line-height: 120%; /* 28.8px */
              font-family: ${isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'Rift, Arial, Helvetica, sans-serif'};
              font-weight: 700;
              margin-bottom: 16px;
            `}
          >
            Registration
          </p>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              gap: 16px;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span
                css={css`
                  color: #fff;
                  font-size: 14px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: 145%; /* 20.3px */
                `}
              >
                Turn on Registration
              </span>
              <span
                css={css`
                  color: #fff;
                  font-size: 12px;
                  font-style: normal;
                  font-weight: 400;
                  line-height: 145%; /* 17.4px */
                `}
              >
                When turned ON, users can use the registration flow to create
                their teams for this competition.
              </span>
            </div>
            <ToggleSection
              title=''
              description=''
              isEnabled={isRegistrationEnabled}
              onClick={() => {
                setIsRegistrationEnabled((o) => !o)
              }}
            />
          </div>
          {/* Separator */}
          {/* <div
            css={css`
              border-bottom: 1px solid #3f4753;
              margin-top: 16px;
              margin-bottom: 16px;
            `}
          /> */}
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 20px;
            align-self: flex-end;
            width: 100%;
            position: sticky;
            bottom: 0;
            background-color: ${colors.HEADER};
            z-index: 100;
            padding-bottom: 16px;
            padding-top: 16px;
          `}
        >
          <Button
            onClick={(e) => {
              e.preventDefault()
              onCancelPressed()
            }}
            label={value && !initialState ? 'Clear' : 'Cancel'}
            variant='secondary'
            disabled={isLoading}
          />
          <Button
            onClick={() => {
              const originalObject = control._fields
              // We need to transform the object to get the values
              const newObject = Object.fromEntries(
                Object.entries(originalObject).map(([key, value]) => [
                  key,
                  value._f.value,
                ])
              )
              onSubmitPressed(newObject)
            }}
            type='submit'
            label='Submit'
            isLoading={isLoading}
          />
        </div>
      </div>
    )
  }

  const IntegrationsForm = () => {
    return (
      <div
        css={css`
          padding-top: 16px;
          padding-left: 16px;
          padding-right: 16px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: flex-start;
          flex: 1;
          height: 100%;
        `}
      >
        <div
          css={css`
            width: 100%;
          `}
        >
          {/* Title */}
          <p
            css={css`
              color: var(--Neutrals-White, #fff);
              font-size: 24px;
              font-style: normal;
              font-weight: 600;
              line-height: 120%; /* 28.8px */
              font-family: ${isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'Rift, Arial, Helvetica, sans-serif'};
              font-weight: 700;
              margin-bottom: 16px;
            `}
          >
            DaySmart
          </p>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              gap: 16px;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span
                css={css`
                  color: #fff;
                  font-size: 14px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: 145%; /* 20.3px */
                `}
              >
                Turn on Registration
              </span>
              <span
                css={css`
                  color: #fff;
                  font-size: 12px;
                  font-style: normal;
                  font-weight: 400;
                  line-height: 145%; /* 17.4px */
                `}
              >
                When turned ON, users can use the registration flow to create
                their teams for this competition.
              </span>
            </div>
            <ToggleSection
              title=''
              description=''
              isEnabled={isRegistrationEnabled}
              onClick={() => {
                setIsRegistrationEnabled((o) => !o)
              }}
            />
          </div>
          {/* Separator */}
          {/* <div
            css={css`
              border-bottom: 1px solid #3f4753;
              margin-top: 16px;
              margin-bottom: 16px;
            `}
          /> */}
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 20px;
            align-self: flex-end;
            width: 100%;
            position: sticky;
            bottom: 0;
            background-color: ${colors.HEADER};
            z-index: 100;
            padding-bottom: 16px;
            padding-top: 16px;
          `}
        >
          <Button
            onClick={(e) => {
              e.preventDefault()
              onCancelPressed()
            }}
            label={value && !initialState ? 'Clear' : 'Cancel'}
            variant='secondary'
            disabled={isLoading}
          />
          <Button
            onClick={() => {
              const originalObject = control._fields
              // We need to transform the object to get the values
              const newObject = Object.fromEntries(
                Object.entries(originalObject).map(([key, value]) => [
                  key,
                  value._f.value,
                ])
              )
              onSubmitPressed(newObject)
            }}
            type='submit'
            label='Submit'
            isLoading={isLoading}
          />
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (initialState && initialState?.starts_at && initialState?.ends_at) {
      setCompetitionDateRange({
        starts_at: dayjs(initialState?.starts_at).utc().format('YYYY-MM-DD'),
        ends_at: dayjs(initialState?.ends_at).utc().format('YYYY-MM-DD'),
      })
    }
  }, [initialState])

  useEffect(() => {
    watch((value, { name, type }) => {
      if (name === 'name_full' && !isEdit) {
        setFormValue('abbreviation', createAbbreviation(value?.name_full))
      }
    })
  }, [watch, isEdit])

  // children: id === '0' ? GeneralSettingsForm() : SettingsForm(),
  const getFormById = (id) => {
    switch (id) {
      case '0':
        return GeneralSettingsForm()
      case '1':
        return SettingsForm()
      case '2':
        return IntegrationsForm()
      default:
        return GeneralSettingsForm()
    }
  }

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            border-bottom: 1px solid ${colors.SOFT_STEEL};
            padding-bottom: 8px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              gap: 16px;
              align-items: center;
            `}
          >
            <h1
              css={css`
                font-size: 36px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'Rift, Arial, Helvetica, sans-serif'};
                font-weight: 700;
                margin-left: 16px;
              `}
            >
              {isEdit
                ? `${isCanlan ? 'Season' : 'Competition'} Details`
                : `Create a New ${isCanlan ? 'Season' : 'Competition'}`}
            </h1>
            {isEdit ? (
              <DeleteModal
                title='Delete Competition'
                message={`Are you sure you want to delete ${initialState?.name_full}? This cannot be undone.`}
                onDelete={async (shouldForce = false) => {
                  const query = {}
                  if (shouldForce) {
                    query.force = 1
                  }
                  await req(`/${ENTITY_TYPES.schedule}/${initialState?.id}`, {
                    method: 'DELETE',
                    query,
                  })
                  window.location.reload()
                }}
                Button={({ ...props }) => (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      pointer-events: auto;
                      cursor: pointer;
                      border: 1px solid ${colors.PRIMARY};
                      border-radius: 36px;
                      width: 36px;
                      height: 36px;
                      padding: 8px;
                    `}
                    {...props}
                  >
                    <button>
                      <Icon faType='far' name='trash' fontSize={16} />
                    </button>
                  </div>
                )}
                useForceDelete={true}
              />
            ) : null}
          </div>
        </div>
        <button
          onClick={() => onCancel()}
          css={css`
            position: fixed;
            right: 40px;
            top: 40px;
            background: none;
            border: none;
          `}
        >
          <CloseOutlined
            fontSize={20}
            style={{
              color: colors.WHITE,
              fontSize: 20,
            }}
          />
        </button>
        <Tabs
          defaultActiveKey='0'
          tabPosition={'left'}
          style={css`
            display: flex;
            flex: 1;
            height: 100%;
          `}
          items={new Array(2).fill(null).map((_, i) => {
            const id = String(i)
            return {
              label: labelById[id],
              key: id,
              children: getFormById(id),
            }
          })}
        />
      </div>
    </>
  )
}

export default NewScheduleForm
